import { createFileRoute } from "@tanstack/react-router";
import { loadIntegration } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";

export const Route = createFileRoute("/integrations/$category/$slug")({
  loader: ({ params }) => loadIntegration({ data: { slug: params.slug, category: params.category } }),
  head: ({ loaderData }) => ({
    meta: loaderData
      ? seo({
          title: loaderData.seo_title || loaderData.app || loaderData.title,
          description: loaderData.seo_description || loaderData.description,
          image: loaderData.image || loaderData.featured_image,
          url: `/integrations/${loaderData._category}/${loaderData.slug}`,
        })
      : [],
    links: loaderData ? seoLinks({ url: `/integrations/${loaderData._category}/${loaderData.slug}` }) : [],
  }),
  component: IntegrationPage,
});

function IntegrationPage() {
  const i = Route.useLoaderData();
  const categoryLabel = i._category
    ?.split("-")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

  return (
    <div className="px-4 pb-14 pt-10 md:px-8 md:pt-14">
      <div className="mx-auto max-w-6xl">
        {/* Back link */}
        <a
          href="/integrations"
          className="inline-flex items-center gap-2 border-b border-gray-40 pb-px text-gray-60 transition-all hover:border-gray-80 hover:text-gray-80"
        >
          <svg className="w-[14px]" viewBox="0 0 14 15" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.482 13.259.241 8.018a.823.823 0 0 1 0-1.165L5.482 1.612a.823.823 0 1 1 1.165 1.165L2.812 6.612H14v1.647H2.812l3.835 3.835a.823.823 0 1 1-1.165 1.165Z"
              fill="#3E3A46"
            />
          </svg>
          <span>All connectors</span>
        </a>

        <h1 className="mt-5 max-w-4xl text-31 font-medium leading-tight text-charcoal lg:text-39">
          {i.app || i.title}
        </h1>
        <hr className="mb-8 mt-6 border-gray-40 lg:mb-10 lg:mt-8" />

        {/* Two-column: main + sidebar */}
        <div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
          {/* Sidebar (right on desktop) */}
          <div className="lg:order-last">
            <div className="rounded-3xl border border-gray-30 p-6 shadow-card-2 lg:sticky lg:top-24">
              <div className="flex items-center gap-2">
                {i.image && (
                  <img src={i.image} alt={i.app || i.title} className="h-auto w-10" loading="lazy" />
                )}
                <h2 className="text-xl font-semibold text-gray-80">{i.app || i.title}</h2>
              </div>
              <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-1">
                {categoryLabel && (
                  <div className="leading-snug">
                    <p className="font-semibold text-gray-80">Category:</p>
                    <p className="text-gray-60">{categoryLabel}</p>
                  </div>
                )}
                {i.connectors?.cards.length ? (
                  <div className="leading-snug md:col-span-2 lg:col-span-1">
                    <p className="font-semibold text-gray-80">Connectors:</p>
                    <ul className="mt-2 space-y-1.5 text-sm">
                      {i.connectors.cards.map((connector) => (
                        <li key={connector.url}>
                          <a
                            href={connector.url}
                            className="border-b border-gray-40 pb-px text-gray-60 transition-all hover:border-gray-80 hover:text-gray-80"
                          >
                            {connector.heading}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {i.resources?.url && (
                  <div className="leading-snug">
                    <p className="font-semibold text-gray-80">Resources:</p>
                    <a
                      href={i.resources.url}
                      className="inline-flex items-center gap-2 border-b border-gray-60 pb-px text-gray-60 transition-all hover:border-gray-80 hover:text-gray-80"
                    >
                      <span>{i.resources.text || "Documentation"}</span>
                    </a>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <a
                  href="/lp/request-demo"
                  className="inline-flex rounded-md bg-teal-dark/10 px-5 py-1.5 font-medium text-teal-dark transition-all hover:bg-teal-dark/20 active:bg-teal-dark active:text-white"
                >
                  Book a demo
                </a>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6 lg:gap-8">
              {i.image && (
                <img src={i.image} alt={i.app || i.title} className="size-16 shrink-0 object-contain md:size-20" loading="lazy" />
              )}
              <div className="flex-1">
                <h2 className="mb-2 text-xl font-bold text-gray-80">Overview</h2>
                {i.html && (
                  <div className="content text-xl leading-normal text-gray-80" dangerouslySetInnerHTML={{ __html: i.html }} />
                )}
              </div>
            </div>

            <hr className="mb-8 mt-6 border-gray-40" />
            {i.features && (
              <div className="rounded-3xl bg-blue-20/20 px-5 py-6">
                {i.features.heading && (
                  <h3 className="mb-1.5 text-25 font-medium leading-snug text-blue-40">
                    {i.features.heading}
                  </h3>
                )}
                <ul className="divide-y divide-gray-40 text-gray-80">
                  {i.features.list?.map((item, idx) => (
                    <li key={idx} className="flex gap-2.5 py-2.5">
                      <span className="mt-[3px] text-sm">✅</span>
                      {typeof item === "string" ? (
                        <span>{item}</span>
                      ) : (
                        <div>
                          <p className="font-semibold">{(item as { heading?: string; copy?: string }).heading}</p>
                          <p>{(item as { heading?: string; copy?: string }).copy}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA */}
      {i.cta && (
        <div className="bg-yellow-20 px-4 py-14 md:px-8 lg:py-20">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-9 lg:grid-cols-3 lg:gap-0 xl:grid-cols-2">
              <div className="lg:pr-10 xl:pr-28">
                {i.cta.heading && (
                  <h2 className="text-31 font-medium leading-tight text-gray-80 lg:leading-snug">
                    {i.cta.heading}
                  </h2>
                )}
                {i.cta.button?.url && (
                  <a
                    href={i.cta.button.url}
                    className="mt-6 inline-flex items-center gap-2.5 rounded-md bg-gray-80 px-4 py-1.5 font-medium text-white transition-all hover:bg-gray-90 active:bg-teal-dark active:text-white"
                  >
                    <span>{i.cta.button.text}</span>
                    <svg className="mt-0.5 size-[14px]" viewBox="0 0 14 15" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.518 2.091l5.241 5.241a.823.823 0 0 1 0 1.165l-5.241 5.241a.823.823 0 1 1-1.165-1.165l3.835-3.835H0V7.091h11.188L7.353 3.255a.823.823 0 1 1 1.165-1.164Z"
                        fill="currentColor"
                      />
                    </svg>
                  </a>
                )}
              </div>
              <div className="lg:col-span-2 xl:col-span-1">
                {i.cta.image && (
                  <img
                    src={i.cta.image}
                    alt={i.cta.heading || ""}
                    className="rounded-lg"
                    loading="lazy"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
